import { CreditCard, FileText, Inbox, Tags } from "lucide-react";
import { Suspense, lazy } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

// React.lazy so the settings page costs the entry bundle nothing but its icon.
const ExpenseCategoriesPage = lazy(() => import("./categories"));

/**
 * Expenses — costs, reimbursements, approvals and the rebilling seam that puts
 * a freelancer's invoice on the client's (PRD §8 Item 10, US-10.5 / US-10.7).
 *
 * `/expenses/:id` is fixed by ENTITY_ROUTES: notifications and search results
 * link there, so the list must open that exact expense.
 */
export default defineFeature({
  key: "expenses",
  title: "Expenses",
  entities: ["expense"],
  routes: [
    { path: "expenses", lazy: () => import("./page") },
    { path: "expenses/:id", lazy: () => import("./page") },
  ],
  nav: [
    {
      label: "Expenses",
      to: "/expenses",
      icon: CreditCard,
      group: "finance",
      order: 30,
      permission: "expenses:read",
      description: "Costs, reimbursements and approvals",
    },
  ],
  quickCreate: [
    {
      key: "expenses",
      label: "Expense",
      icon: CreditCard,
      to: "/expenses?new=1",
      permission: "expenses:write",
      description: "Log a cost with its receipt",
    },
  ],
  commands: [
    { key: "expenses.approvals", label: "Approve expenses", icon: Inbox, to: "/expenses?tab=approvals", group: "actions", permission: "expenses:approve", keywords: ["waiting", "submitted"] },
    {
      key: "expenses.rebill",
      label: "Costs to rebill",
      icon: FileText,
      to: "/expenses?tab=rebill",
      group: "actions",
      permission: ["invoices:write", "financials:view"],
      keywords: ["recharge", "pass-through", "markup"],
    },
  ],
  settingsSections: [
    {
      key: "expense-categories",
      label: "Expense categories",
      path: "expense-categories",
      group: "Finance",
      order: 140,
      icon: Tags,
      description: "Cost types, default markup and SAC codes",
      permission: "expenses:read",
      element: (
        <Suspense fallback={<Skeleton className="h-72 w-full" rounded="card" />}>
          <ExpenseCategoriesPage />
        </Suspense>
      ),
    },
  ],
});
